import React from 'react';
import clsx from 'clsx';
import { Grid } from '@shared/components';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import useStyle from './style';
import { Button, Tooltip } from '@material-ui/core';

const noop = () => {};
const defaultState = {
  isFetching: false,
  pageNumber: 1,
  pageSize: 20,
  sortOrder: null,
  sortBy: null,
};

const DataGrid = ({
  paperElevation = 4,
  entries = [],
  isSyncing = false,
  isSyncPaused = false,
  isSyncCompleted = false,
  wasSyncPaused = false,
  onSync = noop,
}) => {
  const classes = useStyle();
  const [state, setState] = React.useState(defaultState);
  React.useEffect(() => {
    setState(prevState => ({
      ...prevState,
      ...defaultState,
    }));
    entries = stableSort(entries, getComparator('ASC', 'extensionId'));
  }, [entries]);

  const columnConfig = React.useMemo(
    () => [
      {
        field: 'type',
        label: 'OPERATION TYPE',
        headerTooltip: 'OPERATION TYPE',
        canSort: true,
        render: row => {
          let operationType = row.type || '';
          operationType =
            operationType.charAt(0).toUpperCase() + operationType.slice(1);
          return (
            <Tooltip title={operationType} placement='top-start'>
              <Typography variant='body2' noWrap>
                {operationType}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'extensionNumber',
        label: 'EXTENSION NUMBER',
        headerTooltip: 'EXTENSION NUMBER',
        canSort: true,
        render: row => (
          <Tooltip title={row.extensionNumber || ''} placement='top-start'>
            <Typography variant='body2' noWrap>
              {row.extensionNumber}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'extensionId',
        label: 'EXTENSION ID',
        headerTooltip: 'EXTENSION ID',
        canSort: true,
        render: row => (
          <Tooltip title={row.extensionId || ''} placement='top-start'>
            <Typography variant='body2' noWrap>
              {row.extensionId}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'desiredPhoneNumber',
        label: 'PHONE NUMBER',
        headerTooltip: 'PHONE NUMBER',
        canSort: true,
        render: row => (
          <Tooltip title={row.desiredPhoneNumber || ''} placement='top-start'>
            <Typography variant='body2' noWrap>
              {row.desiredPhoneNumber}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'phoneNumberName',
        label: 'PHONE NUMBER NAME',
        headerTooltip: 'PHONE NUMBER NAME',
        canSort: true,
        render: row => (
          <Tooltip title={row.phoneNumberName || ''} placement='top-start'>
            <Typography variant='body2' noWrap>
              {row.phoneNumberName}
            </Typography>
          </Tooltip>
        ),
      },
    ],
    []
  );

  const handlePageSizeChange = React.useCallback(pageSize => {
    setState(prevState => ({
      ...prevState,
      pageSize,
      pageNumber: defaultState.pageNumber,
      isFetching: true,
    }));
  }, []);

  const handlePageNumberChange = React.useCallback(pageNumber => {
    setState(prevState => ({
      ...prevState,
      pageNumber,
      isFetching: true,
    }));
  }, []);

  React.useEffect(() => {
    setTimeout(() => {
      setState(prevState => ({
        ...prevState,
        isFetching: false,
      }));
    }, 400);
  }, [state.isFetching, state.pageNumber, state.pageSize]);

  const handleSortChange = React.useCallback(
    (sortBy = null, sortOrder = null) => {
      setState(prevState => ({
        ...prevState,
        sortBy: sortOrder ? sortBy : null,
        sortOrder,
      }));
    },
    []
  );

  const descendingComparator = (a, b, orderBy) => {
    const splittedOrderBy = (orderBy || '').split('.');
    [...Array(splittedOrderBy.length)].map((item, index) => {
      a = (a && a[splittedOrderBy[index]]) || '';
      b = (b && b[splittedOrderBy[index]]) || '';
    });

    if (b < a) {
      return -1;
    }
    if (b > a) {
      return 1;
    }
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === 'DESC'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map(el => el[0]);
  };

  const gridData = stableSort(
    entries,
    getComparator(state.sortOrder, state.sortBy)
  ).slice(
    (state.pageNumber - 1) * state.pageSize,
    (state.pageNumber - 1) * state.pageSize + state.pageSize
  );

  return (
    <Paper className='mr-10 ml-10 p-6 mt-5 mb-10' elevation={paperElevation}>
      <div className='d-flex f-align-center f-justify-between mb-4'>
        <Typography variant='h6' className='ml-2 text-bold'>
          Review Data
        </Typography>
        <Button
          variant='contained'
          color={wasSyncPaused ? 'secondary' : 'primary'}
          disabled={isSyncing || isSyncCompleted || isSyncPaused}
          className={clsx({
            'color-white': wasSyncPaused,
          })}
          onClick={onSync}
        >
          {`${wasSyncPaused ? 'ReSync' : 'Sync'} Changes`}
        </Button>
      </div>
      <Grid
        classes={{
          container: classes.gridPaper,
        }}
        hasLoader
        sortBy={columnConfig.findIndex(column => column.field === state.sortBy)}
        sortOrder={state.sortOrder}
        onSort={handleSortChange}
        isLoading={state.isFetching}
        pageSize={state.pageSize}
        pageNumber={state.pageNumber}
        total={entries.length}
        columns={columnConfig}
        data={gridData}
        hasSelection={false}
        onPageSizeChange={handlePageSizeChange}
        onPageNumberChange={handlePageNumberChange}
        showRowDivider
      />
    </Paper>
  );
};

export default DataGrid;
